import { forwardRef, KeyboardEvent, MouseEvent, ReactElement, ReactNode, Ref, useMemo } from 'react'

import { Asable } from '../../../../types/asable'
import { LinkProps } from '../../../../types/linkProps'
import { getLinkProps } from '../../../../utils/getLinkProps'
import { NavItemState } from '../../types/navItemState'
import { NavItemVariant } from '../../types/navItemVariant'
import { getStateToStyles } from '../../utils/getStateToStyles'
import * as Styled from './styles'

interface NavListItemViewProps extends Asable {
  children: ReactNode
  expanded?: boolean
  focused?: boolean
  href?: string
  onClick?: (event: MouseEvent) => void
  onKeyDown?: (event: KeyboardEvent) => void
  readonly?: boolean
  selected?: boolean
  state: NavItemState
  subItems?: ReactNode
  tabIndex?: number
  to?: string
  rel?: string
  target?: string
  className?: string
  variant?: NavItemVariant
}

export const NavListItemView = forwardRef(
  (
    {
      as,
      children,
      href,
      onClick,
      onKeyDown,
      readonly,
      state,
      subItems,
      tabIndex = 0,
      to,
      rel,
      target,
      variant = 'primary',
      ...rest
    }: NavListItemViewProps,
    forwardedRef: Ref<HTMLDivElement>,
  ): ReactElement => {
    const { backgroundColor, textColor } = getStateToStyles()[variant][state]
    const linkProps: LinkProps | undefined = useMemo(
      () =>
        getLinkProps({
          as,
          href,
          rel,
          target,
          to,
        }),
      [as, href, rel, target, to],
    )

    return (
      <Styled.NavListItemViewWrapper as={as}>
        <Styled.NavListItemView
          {...rest}
          {...linkProps}
          $backgroundColor={backgroundColor}
          $color={textColor}
          onClick={onClick}
          onKeyDown={onKeyDown}
          $readonly={readonly}
          ref={forwardedRef}
          tabIndex={tabIndex}
          // temporary change so designers can override styles easily for presentation pourposes
          className={`NavListItemViewState-${state.charAt(0).toUpperCase() + state.slice(1)} ${rest.className || ''}`}
        >
          {children}
        </Styled.NavListItemView>
        {subItems}
      </Styled.NavListItemViewWrapper>
    )
  },
)
